import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Users, ArrowLeft, Sparkles, Loader2, Rocket, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

export default function IndustryPrompt() {
  const navigate = useNavigate();
  const { isAuthenticated, isIndustry, token, API } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // If already signed in, hop straight to the project workspace
  useEffect(() => {
    try {
      const p = sessionStorage.getItem('pending_project_prompt');
      if (p) setPrompt(p);
    } catch (e) { /* noop */ }
  }, []);

  const submit = async () => {
    const trimmed = prompt.trim();
    if (trimmed.length < 20) {
      toast.error('Please describe your requirement in at least a couple of sentences.');
      return;
    }
    if (isAuthenticated && isIndustry) {
      setSubmitting(true);
      try {
        const res = await fetch(`${API}/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ prompt: trimmed })
        });
        if (res.ok) {
          const data = await res.json();
          navigate(`/industry/projects/${data.id}`);
        } else {
          toast.error('Could not create project');
        }
      } finally { setSubmitting(false); }
      return;
    }
    try { sessionStorage.setItem('pending_project_prompt', trimmed); } catch (e) { /* noop */ }
    navigate('/industry/auth?next=create_project');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <nav className="nav-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md gradient-primary flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-semibold tracking-tight">CollabHive</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">Powered by PM</span>
            </div>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </Button>
          </Link>
        </div>
      </nav>

      <main className="container-main py-10 sm:py-16 max-w-3xl">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <Sparkles className="w-3 h-3" />
            <span>Comprehensive Requirement Brief</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Start Your Project</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Describe your R&amp;D requirement in plain English. Our team will draft a comprehensive
            Requirement Brief — scope, timeline, IP terms, realistic budget — in seconds.
          </p>
        </div>

        <Card className="border-primary/40 shadow-lg animate-fade-in">
          <CardContent className="p-6 sm:p-8 space-y-4">
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., We need to develop a novel cathode material for lithium-ion batteries with 30% higher energy density. Timeline 6 months, budget ~30 lakhs INR. Access to characterization lab required."
              rows={7}
              className="resize-none text-sm"
              autoFocus
              data-testid="project-prompt-input"
            />
            <div className="flex flex-col sm:flex-row justify-between gap-3 items-start sm:items-center">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                Your requirement stays private until you approve the final brief.
              </p>
              <Button
                size="lg"
                onClick={submit}
                disabled={submitting}
                className="w-full sm:w-auto gap-2"
                data-testid="create-project-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
                Generate Brief
              </Button>
            </div>
          </CardContent>
        </Card>

        {!isAuthenticated && (
          <p className="text-center text-xs text-muted-foreground mt-6">
            You&apos;ll create a free CollabHive account after submitting — takes 30 seconds.
          </p>
        )}
      </main>
    </div>
  );
}
